// src/services/invitationsService.js
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  getDoc,
  getDocs,
  deleteField,
  query, 
  where,
  orderBy,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../config/firebase';

export const invitationsService = {
  // Send organizer invitation
  async sendOrganizerInvitation(bookId, inviteeEmail, inviteeUserId, inviterData, bookData) {
    try {
      const invitationData = {
        type: 'organizer',
        bookId,
        bookName: bookData.botName || 'Event',
         bookCategory: bookData.category || bookData.bookCategory || 'Events',
        inviteeEmail,
        inviteeUserId,
        inviterUserId: inviterData.uid,
        inviterName: inviterData.displayName || inviterData.email,
        inviterEmail: inviterData.email,
        status: 'pending',
        role: 'Co-organizer',
        createdAt: serverTimestamp(),
        expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
      };

      const invitationRef = await addDoc(collection(db, 'pb-invitations'), invitationData);
      
      // Create notification for invitee (IMPORTANT: Use userId field)
      await addDoc(collection(db, 'pb-notifications'), {
        userId: inviteeUserId, // This links it to the user
        type: 'organizer_invitation',
        category: 'invitation',
        title: 'Event Organizer Invitation',
        message: `${inviterData.displayName || inviterData.email} invited you to be an organizer for "${bookData.botName}"`,
        data: {
          invitationId: invitationRef.id,
          bookId,
          bookName: bookData.botName
        },
        read: false,
        actionRequired: true,
        responded: false,
        priority: 'high',
        createdAt: serverTimestamp(),
        expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
      });

      return invitationRef.id;
    } catch (error) {
      console.error('Error sending invitation:', error);
      throw error;
    }
  },

  // Get pending invitations for a user
  async getUserInvitations(userId) {
    try {
      const invitationsRef = collection(db, 'pb-invitations');
      const q = query(
        invitationsRef,
        where('inviteeUserId', '==', userId),
        where('status', '==', 'pending'),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting invitations:', error);
      throw error;
    }
  },

  // Accept invitation
  async acceptInvitation(invitationId) {
    try {
      const invitationRef = doc(db, 'pb-invitations', invitationId);
      const invitationSnap = await getDoc(invitationRef);
      
      if (!invitationSnap.exists()) {
        throw new Error('Invitation not found');
      }

      const invitation = invitationSnap.data();
      
      // Prepare update data
      const updateData = {
        status: 'accepted',
        acceptedAt: serverTimestamp()
      };
      
      // If there was a previous error, clear it
      if (invitation.status === 'error') {
        updateData.error = deleteField(); // Clear error field
        updateData.errorAt = deleteField(); // Clear errorAt field
      }
      
      // Update invitation status
      await updateDoc(invitationRef, updateData);
      
      // Wait for Cloud Function to process
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      return true;
    } catch (error) {
      console.error('Error accepting invitation:', error);
      throw error;
    }
  },

  // Decline invitation
  async declineInvitation(invitationId) {
    try {
      const invitationRef = doc(db, 'pb-invitations', invitationId);
      
      await updateDoc(invitationRef, {
        status: 'declined',
        declinedAt: serverTimestamp()
      });
      
      return true;
    } catch (error) {
      console.error('Error declining invitation:', error);
      throw error;
    }
  },

  // Cancel invitation (by book owner)
  async cancelInvitation(invitationId) {
    try {
      const invitationRef = doc(db, 'pb-invitations', invitationId);
      
      await updateDoc(invitationRef, {
        status: 'cancelled',
        cancelledAt: serverTimestamp()
      });
      
      return true;
    } catch (error) {
      console.error('Error cancelling invitation:', error);
      throw error;
    }
  },

  // Remove organizer
  async removeOrganizer(bookId, organizerId) {
    try {
      const bookRef = doc(db, 'pb-books', bookId);
      const bookSnap = await getDoc(bookRef);
      
      if (bookSnap.exists()) {
        const bookData = bookSnap.data();
        const organizers = (bookData.organizers || []).filter(org => org.userId !== organizerId);
        const organizerIds = (bookData.organizerIds || []).filter(id => id !== organizerId);
        
        await updateDoc(bookRef, {
          organizers: organizers,
          organizerIds: organizerIds,
          updatedAt: serverTimestamp()
        });
      }

      // Also update user's organizing list
      const userRef = doc(db, 'pb-users', organizerId);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const userData = userSnap.data();
        const organizingBooks = (userData.organizingBooks || []).filter(id => id !== bookId);
        
        await updateDoc(userRef, {
          organizingBooks: organizingBooks,
          updatedAt: serverTimestamp()
        });
      }

      return true;
    } catch (error) {
      console.error('Error removing organizer:', error);
      throw error;
    }
  },

  // Get pending invitations for a book
  async getBookInvitations(bookId) {
    try {
      const invitationsRef = collection(db, 'pb-invitations');
      const q = query(
        invitationsRef,
        where('bookId', '==', bookId),
        where('status', '==', 'pending'),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting book invitations:', error);
      throw error;
    }
  }
};